import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { AuthPending } from "@/components/signin/auth-card";
import { EmailStep } from "@/components/signin/email-step";
import { useSigninT } from "@/components/signin/signin-i18n";
import {
	fallbackDestination,
	needsAccountChooser,
	parseAuthSearch,
	resolveService,
	wantsExplicitForm,
} from "@/lib/auth-flow";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/")({
	validateSearch: parseAuthSearch,
	component: SignInScreen,
});

/**
 * Экран входа (макет 4a, карточка 1).
 *
 * Перед формой — тихий вопрос «а вы вообще уже вошли?». Сессия IEC ID живёт на
 * `*.iec.exchange` и переживает вкладку, поэтому человек, пришедший из сервиса,
 * чаще всего уже наш: показывать ему пустое поле почты — это заставлять
 * доказывать то, что мы и так знаем.
 *
 * Дальше развилка: чужой сервис — экран «Продолжить как …»; свои поверхности
 * (витрина, админка, прямой заход на `auth.`) уже владеют сессией и уходят
 * сразу на `redirect_to` / главную профиля.
 *
 * `login=true` отменяет проверку: этим параметром сервис говорит «нужна форма»,
 * и спорить с ним нельзя — иначе выйти из аккаунта станет невозможно.
 */
function SignInScreen() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const t = useSigninT();
	const service = resolveService(search);
	const explicitForm = wantsExplicitForm(search);

	const hint = api.auth.sessionHint.useQuery(service ? { serviceId: service.id } : {}, {
		enabled: !explicitForm,
		retry: false,
		// Ответ живёт ровно один переход: сессия могла погаснуть минуту назад.
		staleTime: 0,
		refetchOnWindowFocus: false,
	});
	const me = api.auth.me.useQuery(undefined, {
		enabled: !explicitForm && hint.data?.state === "session",
		retry: false,
	});

	const hasSession = hint.data?.state === "session";

	useEffect(() => {
		if (explicitForm || !hasSession || !me.data) return;
		if (me.data.needsOnboarding) {
			void navigate({ to: "/onboarding", search, replace: true });
			return;
		}
		if (needsAccountChooser(search)) {
			void navigate({ to: "/continue", search, replace: true });
			return;
		}
		window.location.assign(fallbackDestination(search));
	}, [explicitForm, hasSession, me.data, navigate, search]);

	// Ошибку `sessionHint` глотаем осознанно: не ответил — значит сессии нет,
	// и человек просто увидит форму. Ронять на этом экран входа нечем.
	if (!explicitForm && (hint.isPending || hasSession)) {
		return <AuthPending label={t("email.checkingSession")} />;
	}

	return <EmailStep search={search} />;
}
